import { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation } from 'wouter';
import { NotificationList } from './NotificationList';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications, useMarkAsRead, useMarkAllAsRead } from '@/hooks/use-notifications';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'dropdown' | 'drawer';
}

export function NotificationCenter({ isOpen, onClose, mode = 'dropdown' }: NotificationCenterProps) {
  const [, setLocation] = useLocation();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useNotifications(filter);

  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const notifications = data?.pages?.flatMap(page => page?.notifications || []) || [];
  const unreadCount = notifications.filter(n => !n.readAt && !n.read_at).length;

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Close on click outside (dropdown mode only)
  useEffect(() => {
    if (mode !== 'dropdown') return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 10);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose, mode]);

  // Lock body scroll in drawer mode
  useEffect(() => {
    if (isOpen && mode === 'drawer') {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen, mode]);

  const handleNotificationClick = useCallback((notification: any) => {
    const metadata = typeof notification.metadata === 'string'
      ? JSON.parse(notification.metadata)
      : notification.metadata;

    if (metadata?.actionUrl) {
      if (metadata.actionUrl.startsWith('http://') || metadata.actionUrl.startsWith('https://')) {
        window.open(metadata.actionUrl, '_blank');
      } else {
        setLocation(metadata.actionUrl);
      }
      onClose();
    }
  }, [setLocation, onClose]);

  const handleMarkAsRead = useCallback((id: number) => {
    markAsRead.mutate(id);
  }, [markAsRead]);

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead.mutate();
  }, [markAllAsRead]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (!isOpen) return null;

  const headerContent = (
    <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-white">Notifications</h2>
        {unreadCount > 0 && (
          <span className="bg-white text-black text-xs font-bold px-2 py-1 rounded-full">
            {unreadCount}
          </span>
        )}
      </div>
      <button
        onClick={onClose}
        className="text-white/30 hover:text-white/60 transition-colors"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );

  const filterContent = (
    <div className="flex gap-2 p-4 border-b border-white/[0.08]">
      <button
        onClick={() => setFilter('all')}
        className={cn(
          'px-4 py-2 text-sm font-medium rounded-md transition-colors',
          filter === 'all'
            ? 'bg-white/[0.1] text-white'
            : 'text-white/40 hover:text-white hover:bg-white/[0.06]'
        )}
      >
        All
      </button>
      <button
        onClick={() => setFilter('unread')}
        className={cn(
          'px-4 py-2 text-sm font-medium rounded-md transition-colors',
          filter === 'unread'
            ? 'bg-white/[0.1] text-white'
            : 'text-white/40 hover:text-white hover:bg-white/[0.06]'
        )}
      >
        Unread
      </button>
    </div>
  );

  const listContent = (
    <NotificationList
      notifications={notifications}
      isLoading={isLoading}
      hasMore={hasNextPage || false}
      filter={filter}
      onLoadMore={handleLoadMore}
      onNotificationClick={handleNotificationClick}
      onMarkAsRead={handleMarkAsRead}
      onMarkAllAsRead={handleMarkAllAsRead}
    />
  );

  // Drawer mode (mobile)
  if (mode === 'drawer') {
    return (
      <div className="fixed inset-0 z-[100]">
        <div
          className="absolute inset-0 bg-black/80 animate-in fade-in duration-200"
          onClick={onClose}
        />
        <div className="absolute bottom-0 left-0 right-0 bg-[#171717] rounded-t-2xl border-t border-white/[0.08] animate-in slide-in-from-bottom duration-300 flex flex-col" style={{ maxHeight: '85vh' }}>
          <div className="flex justify-center pt-3 pb-1 shrink-0">
            <div className="w-10 h-1 rounded-full bg-white/20" />
          </div>
          <div className="shrink-0">{headerContent}</div>
          <div className="shrink-0">{filterContent}</div>
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {listContent}
          </div>
          <div className="h-8 shrink-0" />
        </div>
      </div>
    );
  }

  // Dropdown mode (desktop)
  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-96 rounded-2xl shadow-xl border border-white/[0.08] z-50 bg-[#171717]"
    >
      {headerContent}
      {filterContent}
      <ScrollArea className="h-[500px]">
        {listContent}
      </ScrollArea>
    </div>
  );
}
